import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [confirming, setConfirming] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const plan =
      params.get("plan") ||
      localStorage.getItem("toothpix_pending_plan") ||
      "clinic";
    const run = async () => {
      try {
        await api.post("/billing/mark-paid", { plan });
        await refreshUser();
        localStorage.removeItem("toothpix_pending_plan");
      } catch (e) {
        setError(formatErr(e.response?.data?.detail) || "We couldn't confirm your payment automatically.");
      } finally {
        setConfirming(false);
      }
    };
    run();
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen bg-bone-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-bone-200 p-10 text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
          <span className="font-heading text-2xl text-forest-700">Toothpix</span>
        </div>
        {confirming ? (
          <>
            <div className="overline mb-3">Confirming</div>
            <h1 className="font-heading text-3xl text-forest-700">Activating your plan…</h1>
            <p className="text-sm text-forest-400 mt-3">One moment while we set you up.</p>
          </>
        ) : error ? (
          <>
            <div className="overline mb-3">Action needed</div>
            <h1 className="font-heading text-3xl text-forest-700">Almost there</h1>
            <p className="text-sm text-forest-400 mt-3">{error}</p>
            <button onClick={() => navigate("/dashboard")} className="btn-primary mt-8" data-testid="ps-dashboard">
              Go to dashboard <ArrowRight size={14} />
            </button>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-forest-600 text-white flex items-center justify-center mx-auto" data-testid="ps-success">
              <Check size={24} />
            </div>
            <h1 className="font-heading text-3xl text-forest-700 mt-6">You're all set.</h1>
            <p className="text-sm text-forest-400 mt-3">
              Your plan is active. Patient quota refreshed. Share your link and watch the leads come in.
            </p>
            <button onClick={() => navigate("/dashboard")} className="btn-primary mt-8" data-testid="ps-go-dashboard">
              Open dashboard <ArrowRight size={14} />
            </button>
            <p className="mt-8 text-[11px] text-forest-400">
              Receipt sent to your email by Stripe. Need help? <Link to="/" className="underline">hello@toothpix.ai</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
